// domain/sparepartImport — baca file Isx/Odoo jadi baris siap impor (murni, tested).
// Pencocokan & tulis-nya di DB (RPC import_spareparts, migrasi 0086) supaya 10.000 SKU
// selesai dalam satu perintah SQL, bukan seribuan permintaan HTTP.
// harga/stok null = "tidak ada di file" → server MEMPERTAHANKAN nilai lama. Jangan diganti 0:
// satu file daftar harga tanpa kolom qty akan menolkan stok seluruh katalog.
export interface ImportRow { barcode: string; nama: string; harga: number | null; stok: number | null; kategori: string }

/** Angka dari sel spreadsheet. HARUS sama aturannya dengan sp_num di migrasi 0087.
 *  Titik/koma ribuan wajib dikenali: `Number("480.000")` = 480, jadi cara polos menyimpan
 *  harga 1000x lebih kecil — dan modal tiket ikut salah 1000x.
 *  null = sel kosong / tak terbaca → biarkan server mempertahankan nilai lama. */
const num = (s: string): number | null => {
  let t = String(s ?? '').trim();
  if (!t) return null;
  t = t.replace(/[^0-9,.-]/g, '');                                    // buang "Rp", spasi, dll
  if (/^-?\d{1,3}(\.\d{3})+$/.test(t)) t = t.replace(/\./g, '');      // 1.234.567 = ribuan
  else if (/^-?\d{1,3}(,\d{3})+$/.test(t)) t = t.replace(/,/g, '');   // 1,234,567 = ribuan
  else t = t.replace(',', '.');                                       // 180,5 = desimal
  if (!/^-?\d+(\.\d+)?$/.test(t)) return null;                        // tak jelas → jangan menebak angka uang
  return Math.max(0, Math.floor(Number(t)));                          // minus (stok kelebihan jual) → 0
};

/** Parse CSV (koma, dukung field ber-tanda-kutip). Baris pertama = header. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = []; let row: string[] = []; let cell = ''; let q = false;
  const s = text.replace(/\r\n?/g, '\n');
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (q) {
      if (c === '"') { if (s[i + 1] === '"') { cell += '"'; i++; } else q = false; }
      else cell += c;
    } else if (c === '"') q = true;
    else if (c === ',') { row.push(cell); cell = ''; }
    else if (c === '\n') { row.push(cell); rows.push(row); row = []; cell = ''; }
    else cell += c;
  }
  if (cell.length || row.length) { row.push(cell); rows.push(row); }
  return rows.filter((r) => r.some((x) => x.trim() !== ''));
}

const ALIAS: Record<keyof ImportRow, string[]> = {
  barcode: ['barcode', 'kode', 'sku', 'code', 'kode barang', 'kodebarang'],
  nama: ['name', 'nama', 'product', 'product name', 'nama barang', 'deskripsi', 'description'],
  harga: ['harga', 'price', 'sales price', 'harga jual', 'unit price'],
  stok: ['stok', 'stock', 'qty', 'quantity', 'quantity on hand', 'on hand', 'qty on hand'],
  kategori: ['kategori', 'category', 'product category', 'kategory'],
};

/** Deteksi index kolom dari baris header (alias Isx/Odoo, case-insensitive). -1 bila tak ada. */
export function mapColumns(header: string[]): Record<keyof ImportRow, number> {
  const h = header.map((x) => x.trim().toLowerCase());
  const find = (keys: string[]): number => h.findIndex((c) => keys.includes(c));
  return { barcode: find(ALIAS.barcode), nama: find(ALIAS.nama), harga: find(ALIAS.harga), stok: find(ALIAS.stok), kategori: find(ALIAS.kategori) };
}

// ---------- Layout Isx (laporan Saldo Stock, bukan tabel rapi) ----------
// Export Isx = laporan cetak: judul di atas, kolom berjarak (sel kosong di antara), judul
// kelompok sebaris sendiri = kategori, dan baris subtotal. Dipadatkan dulu, lalu dicocokkan
// bentuk barisnya: [nama, kode, qty, satuan, harga, (saldo)].
const numeric = (s: string): boolean => /^-?[\d.,]+$/.test(s.trim()) && /\d/.test(s);
const compact = (r: string[]): string[] => r.map((c) => String(c ?? '').trim()).filter((c) => c !== '');
const isxDataRow = (c: string[]): boolean =>
  c.length >= 5 && !numeric(c[0]!) && !numeric(c[1]!) && numeric(c[2]!) && !numeric(c[3]!) && numeric(c[4]!);

/** Grid ini laporan Isx? (butuh ≥3 baris berbentuk data — 1-2 bisa kebetulan). */
export function isIsxGrid(grid: string[][]): boolean {
  return grid.filter((r) => isxDataRow(compact(r))).length >= 3;
}

/**
 * Baris header laporan Isx (memuat "Kode" DAN "Nama Barang") → urutan kolomnya.
 * `null` = baris ini bukan header. `true` = Kode di depan, `false` = Nama di depan.
 *
 * Isx mengekspor DUA urutan berbeda: `data isx.xls` = Nama|Kode, `sptall.xls` = Kode|Nama.
 * Karena itu urutannya dibaca dari header, tidak dipatok — mematoknya berarti satu dari dua
 * file selalu masuk terbalik (nama barang tersimpan di kolom barcode).
 */
const isxHeaderOrder = (c: string[]): boolean | null => {
  const t = c.map((x) => x.toLowerCase());
  const iKode = t.findIndex((x) => x === 'kode' || x.startsWith('kode '));
  const iNama = t.findIndex((x) => x.includes('nama') || x === 'name');
  if (iKode < 0 || iNama < 0) return null;
  return iKode < iNama;
};

/**
 * Laporan Isx → ImportRow[]. Urutan sel setelah dirapatkan: KODE · NAMA BARANG · QTY · SATUAN · HARGA.
 *
 * Dulu kode & nama TERTUKAR di sini (barcode diisi nama, nama diisi kode) — 4.392 SKU milik Apo
 * tersimpan terbalik sampai ketahuan dari layar. Urutan di bawah mengikuti header file aslinya.
 *
 * Kategori hanya diambil dari judul kelompok SETELAH baris header; sebelum itu yang ada hanyalah
 * kop laporan ("I-TECH & PART", "Saldo Stock", "Per Tgl. …") — dan itu sempat terpasang sebagai
 * kategori SELURUH katalog.
 */
export function rowsFromIsx(grid: string[][]): ImportRow[] {
  const out: ImportRow[] = [];
  let kategori = '';
  let kodeDulu = false;   // default: layout lama (Nama Barang di depan)
  let adaHeader = false;
  for (const r of grid) {
    const c = compact(r);
    if (isxDataRow(c)) {
      const bc = kodeDulu ? c[0]! : c[1]!;
      const nm = kodeDulu ? c[1]! : c[0]!;
      out.push({ barcode: bc, nama: nm, harga: num(c[4]!), stok: num(c[2]!), kategori });
      continue;
    }
    const urut = isxHeaderOrder(c);
    if (urut !== null) { kodeDulu = urut; adaHeader = true; kategori = ''; continue; }
    if (adaHeader && c.length === 1 && !numeric(c[0]!)) kategori = c[0]!; // judul kelompok yang sah
  }
  return out;
}

export interface ParsedImport { rows: ImportRow[]; cols: Record<keyof ImportRow, number>; header: string[]; layout: 'isx' | 'tabel' }

/** Grid (CSV / sheet .xls-.xlsx) → ImportRow[]. Auto-deteksi laporan Isx vs tabel berheader (Odoo dll).
 *  Tabel: header dicari di 10 baris pertama (laporan sering ada blok judul di atasnya). */
export function rowsFromGrid(grid: string[][]): ParsedImport {
  if (isIsxGrid(grid)) return { rows: rowsFromIsx(grid), cols: { barcode: -1, nama: -1, harga: 4, stok: 2, kategori: -1 }, header: [], layout: 'isx' };
  let hi = grid.findIndex((r, i) => i < 10 && mapColumns(r).barcode >= 0);
  if (hi < 0) hi = 0;
  const header = grid[hi] ?? [];
  const cols = mapColumns(header);
  const at = (r: string[], i: number): string => (i >= 0 ? (r[i] ?? '').trim() : '');
  const rows = grid.slice(hi + 1).map((r) => ({
    barcode: at(r, cols.barcode), nama: at(r, cols.nama), harga: num(at(r, cols.harga)), stok: num(at(r, cols.stok)), kategori: at(r, cols.kategori),
  })).filter((x) => x.barcode || x.nama);
  return { rows, cols, header, layout: 'tabel' };
}

/** CSV → ImportRow[] (skip header). barcode/nama wajib minimal salah satu; kolom hilang → default. */
export function rowsFromCsv(text: string): ParsedImport {
  return rowsFromGrid(parseCsv(text));
}

/** Buang barcode kembar DALAM satu file (yang terakhir menang), tak peduli huruf besar/kecil.
 *  Wajib dilakukan sebelum dipecah jadi beberapa kiriman: kalau barcode sama jatuh di dua
 *  potongan berbeda, DB tak bisa lagi menganggapnya satu baris dan hitungannya jadi
 *  "1 baru + 1 update" padahal cuma satu barang. Baris tanpa barcode dibiarkan lewat —
 *  DB yang melaporkannya sebagai "dilewati". */
export function dedupByBarcode(rows: ImportRow[]): { rows: ImportRow[]; merged: number } {
  const seen = new Map<string, ImportRow>();
  const noBarcode: ImportRow[] = [];
  for (const r of rows) {
    if (!r.barcode) { noBarcode.push(r); continue; }
    seen.set(r.barcode.toLowerCase(), r);
  }
  return { rows: [...seen.values(), ...noBarcode], merged: rows.length - noBarcode.length - seen.size };
}
